import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../../shared/components/Layout';
import Modal from '../../shared/components/Modal';
import Pagination from '../../shared/components/Pagination';
import { useAuth } from '../auth/AuthContext';
import { extractErrorMessage } from '../../shared/utils/errors';
import { formatNetworkLabel } from '../../shared/utils/networkLabel';
import {
  PaginatedResponse,
  WithdrawalOperation,
  WithdrawalStatus,
} from '../../shared/types';
import { formatUserLabel, useUserNames } from '../../shared/hooks/useUserNames';
import CryptoAmountWithUsd from '../../shared/components/CryptoAmountWithUsd';
import {
  WithdrawalFilters,
  approveWithdrawal,
  checkWithdrawalStatus,
  getWithdrawal,
  listWithdrawals,
  rejectWithdrawal,
} from './api';

const STATUS_OPTIONS: { value: WithdrawalStatus | ''; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'pending_confirmation', label: 'Pending confirmation' },
  { value: 'pending', label: 'Pending review' },
  { value: 'processing', label: 'Processing' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'rejected', label: 'Rejected' },
];

const CURRENCY_OPTIONS = ['', 'BTC', 'ETH', 'USDT', 'TRX'];

const SMALLEST_UNIT_DIVISOR = 100_000_000;

const formatCryptoAmount = (amount: number | string | null | undefined): string => {
  if (amount === null || amount === undefined) return '0';
  const value = typeof amount === 'string' ? Number(amount) : amount;
  if (!Number.isFinite(value)) return '0';
  const decimal = value / SMALLEST_UNIT_DIVISOR;
  return decimal.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 8,
  });
};

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
};

const statusBadge = (status: WithdrawalStatus): string => {
  switch (status) {
    case 'completed':
      return 'badge badge-success';
    case 'pending':
    case 'pending_confirmation':
    case 'processing':
      return 'badge badge-warning';
    case 'failed':
    case 'rejected':
      return 'badge badge-danger';
    case 'cancelled':
      return 'badge bg-slate-700/40 text-slate-300 border border-slate-600';
    default:
      return 'badge';
  }
};

const WithdrawalsPage: React.FC = () => {
  const { admin } = useAuth();
  const [withdrawals, setWithdrawals] = useState<PaginatedResponse<WithdrawalOperation> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [filters, setFilters] = useState<WithdrawalFilters>({});
  const [draftFilters, setDraftFilters] = useState<WithdrawalFilters>({});

  const [detail, setDetail] = useState<WithdrawalOperation | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionInFlight, setActionInFlight] = useState<'approve' | 'reject' | 'check' | null>(null);
  const [checkBanner, setCheckBanner] = useState<{ tone: 'info' | 'success' | 'warning'; message: string } | null>(null);

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const canReview =
    admin?.role === 'super_admin' ||
    admin?.backoffice_role === 'administrator' ||
    (admin?.permissions?.includes('withdrawal.review') ?? false);

  const load = useCallback(async (page: number, filtersToUse: WithdrawalFilters) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listWithdrawals({ ...filtersToUse, page, per_page: 20 });
      setWithdrawals(data);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load withdrawals'));
      setWithdrawals(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load(currentPage, filters);
  }, [currentPage, filters, load]);

  const applyFilters = () => {
    setFilters(draftFilters);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setDraftFilters({});
    setFilters({});
    setCurrentPage(1);
  };

  const openDetail = async (withdrawal: WithdrawalOperation) => {
    setDetail(withdrawal);
    setDetailError(null);
    setActionError(null);
    setCheckBanner(null);
    setDetailLoading(true);
    try {
      const fresh = await getWithdrawal(withdrawal.id);
      setDetail(fresh);
    } catch (err) {
      setDetailError(extractErrorMessage(err, 'Failed to load withdrawal'));
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetail(null);
    setDetailError(null);
    setActionError(null);
    setCheckBanner(null);
  };

  const filterByUser = (userId: number) => {
    const value = String(userId);
    setDraftFilters((p) => ({ ...p, user_id: value }));
    setFilters((p) => ({ ...p, user_id: value }));
    setCurrentPage(1);
  };

  const handleApprove = async () => {
    if (!detail) return;
    setActionError(null);
    setActionInFlight('approve');
    try {
      const updated = await approveWithdrawal(detail.id);
      setDetail(updated);
      await load(currentPage, filters);
    } catch (err) {
      setActionError(extractErrorMessage(err, 'Failed to approve withdrawal'));
    } finally {
      setActionInFlight(null);
    }
  };

  const handleReject = async () => {
    if (!detail) return;
    setActionError(null);
    setActionInFlight('reject');
    try {
      const updated = await rejectWithdrawal(detail.id, rejectReason.trim() || undefined);
      setDetail(updated);
      setRejectModalOpen(false);
      setRejectReason('');
      await load(currentPage, filters);
    } catch (err) {
      setActionError(extractErrorMessage(err, 'Failed to reject withdrawal'));
    } finally {
      setActionInFlight(null);
    }
  };

  const handleCheckStatus = async () => {
    if (!detail) return;
    setActionError(null);
    setCheckBanner(null);
    setActionInFlight('check');
    try {
      const result = await checkWithdrawalStatus(detail.id);
      setDetail(result.withdrawal);

      const tone =
        result.state === 'confirmed' ? 'success' : result.state === 'failed' ? 'warning' : 'info';
      const message =
        result.state === 'confirmed'
          ? result.changed
            ? 'Confirmed on-chain — withdrawal completed and the user has been notified.'
            : 'Already confirmed on-chain.'
          : result.state === 'failed'
            ? result.changed
              ? 'On-chain transaction failed — funds were returned to the user.'
              : 'Already marked failed.'
            : 'Still pending on-chain. Try again later.';
      setCheckBanner({ tone, message });

      if (result.changed) {
        await load(currentPage, filters);
      }
    } catch (err) {
      setActionError(extractErrorMessage(err, 'Failed to check on-chain status'));
    } finally {
      setActionInFlight(null);
    }
  };

  const userIds: number[] = [];
  if (withdrawals?.data) {
    for (const w of withdrawals.data) userIds.push(w.user_id);
  }
  if (detail) userIds.push(detail.user_id);
  const userNames = useUserNames(userIds);

  const reviewable = detail
    ? detail.status === 'pending' && !detail.is_internal
    : false;

  const checkable = detail
    ? detail.status === 'processing' && !detail.is_internal && Boolean(detail.tx_hash)
    : false;

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Withdrawal Review</h1>
            <p className="text-slate-400 mt-1">
              Only withdrawals in <span className="text-slate-300">Pending review</span> appear here — after the user
              confirms email/phone OTP or 2FA. Unverified requests stay in Pending confirmation.
            </p>
          </div>
        </div>

        <div className="card p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">User ID</label>
            <input
              type="number"
              min={1}
              value={draftFilters.user_id ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, user_id: e.target.value || undefined }))
              }
              className="input py-2"
              placeholder="e.g. 42"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Status</label>
            <select
              value={draftFilters.status ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({
                  ...p,
                  status: (e.target.value || undefined) as WithdrawalStatus | undefined,
                }))
              }
              className="input py-2"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Currency</label>
            <select
              value={draftFilters.currency ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, currency: e.target.value || undefined }))
              }
              className="input py-2"
            >
              {CURRENCY_OPTIONS.map((c) => (
                <option key={c || 'all-cur'} value={c}>
                  {c || 'Any'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Address</label>
            <input
              type="text"
              value={draftFilters.address ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, address: e.target.value || undefined }))
              }
              className="input py-2 font-mono text-xs"
              placeholder="0x… / bc1…"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Reference</label>
            <input
              type="text"
              value={draftFilters.reference_id ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, reference_id: e.target.value || undefined }))
              }
              className="input py-2 font-mono text-xs"
              placeholder="WD_…"
            />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={applyFilters} className="btn btn-primary py-2 flex-1">
              Apply
            </button>
            <button type="button" onClick={clearFilters} className="btn btn-secondary py-2">
              Clear
            </button>
          </div>
        </div>

        <div className="card overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-red-400">{error}</p>
            </div>
          ) : !withdrawals?.data.length ? (
            <div className="p-12 text-center text-slate-400">No withdrawals found.</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800/50">
                    <tr>
                      <th className="table-header">ID</th>
                      <th className="table-header">User</th>
                      <th className="table-header">Currency</th>
                      <th className="table-header">Network</th>
                      <th className="table-header text-right">Amount</th>
                      <th className="table-header text-right">Fee</th>
                      <th className="table-header">Address</th>
                      <th className="table-header">Type</th>
                      <th className="table-header">Status</th>
                      <th className="table-header">Created</th>
                      <th className="table-header text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {withdrawals.data.map((withdrawal) => (
                      <tr key={withdrawal.id} className="hover:bg-slate-800/30 transition-colors">
                        <td className="table-cell font-mono text-indigo-400">#{withdrawal.id}</td>
                        <td className="table-cell">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); filterByUser(withdrawal.user_id); }}
                            className="text-sky-400 hover:text-sky-300 hover:underline transition-colors"
                            title={`Show all withdrawals for user #${withdrawal.user_id}`}
                          >
                            {formatUserLabel(withdrawal.user_id, userNames)}
                          </button>
                          <p className="text-xs text-slate-500 font-mono mt-0.5">#{withdrawal.user_id}</p>
                        </td>
                        <td className="table-cell text-white whitespace-nowrap font-medium">
                          {withdrawal.currency}
                        </td>
                        <td className="table-cell text-slate-400 font-mono text-xs">
                          {formatNetworkLabel(withdrawal.network)}
                        </td>
                        <td className="table-cell text-right text-slate-200 font-mono">
                          {formatCryptoAmount(withdrawal.amount)}{' '}
                          <span className="text-xs text-slate-500">{withdrawal.currency}</span>
                        </td>
                        <td className="table-cell text-right text-slate-400 font-mono">
                          <CryptoAmountWithUsd
                            units={withdrawal.fee}
                            currency={withdrawal.currency}
                            showCurrency={false}
                          />
                        </td>
                        <td className="table-cell font-mono text-xs text-slate-400 max-w-[200px] truncate" title={withdrawal.address}>
                          {withdrawal.address}
                        </td>
                        <td className="table-cell">
                          {withdrawal.is_internal ? (
                            <span className="badge bg-sky-500/15 text-sky-300 border border-sky-500/40">Internal</span>
                          ) : (
                            <span className="badge bg-slate-700/40 text-slate-300 border border-slate-600">External</span>
                          )}
                        </td>
                        <td className="table-cell">
                          <span className={statusBadge(withdrawal.status)}>{withdrawal.status.replace('_', ' ')}</span>
                        </td>
                        <td className="table-cell text-slate-400 whitespace-nowrap">
                          {formatDateTime(withdrawal.created_at)}
                        </td>
                        <td className="table-cell text-right">
                          <button
                            onClick={() => openDetail(withdrawal)}
                            className="px-3 py-1.5 text-xs font-medium text-indigo-400 hover:text-indigo-300 bg-indigo-500/10 hover:bg-indigo-500/20 rounded-lg transition-all"
                          >
                            Review
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {withdrawals.last_page > 1 && (
                <div className="px-6 py-4 border-t border-slate-800">
                  <Pagination
                    currentPage={currentPage}
                    lastPage={withdrawals.last_page}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Modal
        isOpen={detail !== null}
        onClose={closeDetail}
        title={detail ? `Withdrawal #${detail.id}` : ''}
      >
        {detail && (
          <div className="space-y-4 text-sm">
            {detailError && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
                {detailError}
              </div>
            )}
            {actionError && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
                {actionError}
              </div>
            )}
            {checkBanner && (
              <div
                className={`p-3 border rounded-lg text-sm ${
                  checkBanner.tone === 'success'
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                    : checkBanner.tone === 'warning'
                      ? 'bg-amber-500/10 border-amber-500/30 text-amber-300'
                      : 'bg-sky-500/10 border-sky-500/30 text-sky-300'
                }`}
              >
                {checkBanner.message}
              </div>
            )}
            {detailLoading && (
              <div className="text-slate-400">Refreshing…</div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <DetailRow
                label="User"
                value={(
                  <span>
                    <button
                      type="button"
                      onClick={() => { filterByUser(detail.user_id); closeDetail(); }}
                      className="text-sky-400 hover:text-sky-300 hover:underline transition-colors"
                      title={`Show all withdrawals for user #${detail.user_id}`}
                    >
                      {formatUserLabel(detail.user_id, userNames)}
                    </button>
                    <span className="text-xs text-slate-500 font-mono ml-2">#{detail.user_id}</span>
                  </span>
                )}
              />
              <DetailRow
                label="Status"
                value={<span className={statusBadge(detail.status)}>{detail.status.replace('_', ' ')}</span>}
              />
              <DetailRow label="Currency" value={detail.currency} />
              <DetailRow label="Network" value={formatNetworkLabel(detail.network)} />
              <DetailRow label="Amount" value={`${formatCryptoAmount(detail.amount)} ${detail.currency}`} />
              <DetailRow
                label="Transaction fee (visible)"
                value={<CryptoAmountWithUsd units={detail.transaction_fee} currency={detail.currency} />}
              />
              <DetailRow
                label="Hidden fee"
                value={<CryptoAmountWithUsd units={detail.hidden_fee} currency={detail.currency} />}
              />
              <DetailRow
                label="Total fee"
                value={<CryptoAmountWithUsd units={detail.fee} currency={detail.currency} />}
              />
              <DetailRow label="Net to address" value={`${formatCryptoAmount(detail.amount - detail.fee)} ${detail.currency}`} />
              <DetailRow label="Type" value={detail.is_internal ? 'Internal transfer' : 'External'} />
              {detail.recipient_user_id && (
                <DetailRow label="Recipient user" value={`#${detail.recipient_user_id}`} />
              )}
              <DetailRow
                label="Destination"
                value={<span className="font-mono text-xs break-all">{detail.address}</span>}
              />
              <DetailRow
                label="Reference"
                value={<span className="font-mono text-xs">{detail.reference_id}</span>}
              />
              <DetailRow
                label="Tx hash"
                value={detail.tx_hash ? <span className="font-mono text-xs break-all">{detail.tx_hash}</span> : '—'}
              />
              <DetailRow label="Created at" value={formatDateTime(detail.created_at)} />
              <DetailRow label="OTP verified at" value={formatDateTime(detail.otp_verified_at)} />
              <DetailRow label="Processed at" value={formatDateTime(detail.processed_at)} />
              <DetailRow label="Reviewed at" value={formatDateTime(detail.reviewed_at)} />
              {detail.reviewed_by_admin_id && (
                <DetailRow label="Reviewed by admin" value={`#${detail.reviewed_by_admin_id}`} />
              )}
              {detail.broadcasted_at && (
                <DetailRow label="Broadcasted at" value={formatDateTime(detail.broadcasted_at)} />
              )}
              {detail.broadcast_provider && (
                <DetailRow label="Provider" value={detail.broadcast_provider} />
              )}
              {detail.hot_wallet_id && (
                <DetailRow label="Hot wallet" value={`#${detail.hot_wallet_id}`} />
              )}
              {detail.chain_status_checked_at && (
                <DetailRow label="Last status check" value={formatDateTime(detail.chain_status_checked_at)} />
              )}
            </div>

            {detail.error_message && (
              <div>
                <p className="text-xs font-medium text-slate-400 mb-1">Notes / Error</p>
                <pre className="p-3 bg-slate-800/60 border border-red-500/30 rounded-lg text-red-300 text-xs whitespace-pre-wrap break-all">
                  {detail.error_message}
                </pre>
              </div>
            )}

            {canReview && reviewable && (
              <div className="flex gap-3 pt-4 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => { setRejectReason(''); setRejectModalOpen(true); }}
                  disabled={actionInFlight !== null}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-red-500/10 border border-red-500/40 text-red-300 hover:bg-red-500/20 transition-all font-medium disabled:opacity-50"
                >
                  Reject
                </button>
                <button
                  type="button"
                  onClick={handleApprove}
                  disabled={actionInFlight !== null}
                  className="flex-1 btn btn-primary disabled:opacity-50"
                >
                  {actionInFlight === 'approve' ? 'Approving…' : 'Approve & broadcast'}
                </button>
              </div>
            )}

            {checkable && (
              <div className="flex flex-col gap-2 pt-4 border-t border-slate-800">
                <p className="text-xs text-slate-400">
                  This withdrawal has been broadcasted on-chain. Funds remain in escrow until the
                  network confirms the transaction.
                </p>
                <button
                  type="button"
                  onClick={handleCheckStatus}
                  disabled={actionInFlight !== null}
                  className="btn btn-primary disabled:opacity-50"
                >
                  {actionInFlight === 'check' ? 'Checking…' : 'Check on-chain status'}
                </button>
              </div>
            )}

            {!canReview && reviewable && (
              <p className="text-xs text-slate-500 pt-3 border-t border-slate-800">
                You don't have the <span className="font-mono">withdrawal.review</span> permission.
              </p>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={rejectModalOpen}
        onClose={() => { if (actionInFlight !== 'reject') setRejectModalOpen(false); }}
        title="Reject withdrawal"
      >
        <form
          onSubmit={(e) => { e.preventDefault(); handleReject(); }}
          className="space-y-4 text-sm"
        >
          <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-300 text-xs">
            Rejecting will release the held funds back to the user's available balance and mark the withdrawal as rejected. The user will be notified.
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">
              Reason (visible in audit log; surfaced to the user)
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              className="input"
              placeholder="e.g. Destination address flagged by compliance"
            />
          </div>
          {actionError && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {actionError}
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => setRejectModalOpen(false)}
              className="flex-1 btn btn-secondary"
              disabled={actionInFlight === 'reject'}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={actionInFlight === 'reject'}
              className="flex-1 px-4 py-2.5 rounded-lg bg-red-500/20 border border-red-500/40 text-red-200 hover:bg-red-500/30 transition-all font-medium disabled:opacity-50"
            >
              {actionInFlight === 'reject' ? 'Rejecting…' : 'Reject withdrawal'}
            </button>
          </div>
        </form>
      </Modal>
    </Layout>
  );
};

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
    <p className="text-slate-200 mt-1 break-all">{value}</p>
  </div>
);

export default WithdrawalsPage;
